import { ContentSwitcher, SingleSelectDropdown } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { DatasetUploadListScope, UploadStatusGroup } from "@/api";
import { hatcheryDropdownModes } from "@/app/theme";
import { FilterBar, type FilterBarChip } from "@/components/filter";
import { datasetUploadFilterChips, type DatasetUploadFilterState } from "./datasetUploadFilters";
import { uploadStatusGroups } from "./statusFilterGroups";

// 드롭다운의 "전체"(해제) 값. c6 단일 선택은 빈 문자열을 중립 값으로 쓴다(neutralValue).
const STATUS_ALL_VALUE = "";

const uploadScopeOptions = [
  { value: "me", label: "내 업로드" },
  { value: "all", label: "전체 업로드" },
];

type DatasetUploadFilterBarProps = {
  // 업로드 범위 스위처(내/전체)는 admin 에게만 보인다. 일반 유저는 "내 업로드" 로 고정되고
  // 스위처가 숨겨진다.
  showScope: boolean;
  filters: DatasetUploadFilterState;
  onChange: (next: DatasetUploadFilterState) => void;
};

// 업로드 현황의 조회 축. 공통 `FilterBar` 셸(rules.md §17)을 쓴다.
//   - 행 1(md): 범위 `ContentSwitcher` + 상태 facet 드롭다운.
//   - 행 2: 상태 칩·초기화.
// 컨트롤과 칩은 같은 상태의 두 표현이다(칩 ✕ = 조건 해제). 상태는 패널(URL)이 소유한다. 이
// 컴포넌트는 표시와 변경 보고만 한다. 결과 수·새로고침은 목록 상단 띠가 가진다.
export function DatasetUploadFilterBar({
  showScope,
  filters,
  onChange,
}: DatasetUploadFilterBarProps): ReactNode {
  const statusOptions = [
    { value: STATUS_ALL_VALUE, label: "전체" },
    ...uploadStatusGroups
      .filter((group) => group.value !== "all")
      .map((group) => ({ value: group.value, label: group.label })),
  ];
  const handleStatusGroupChange = (statusGroup: UploadStatusGroup): void => {
    onChange({ ...filters, statusGroup });
  };
  const chips: FilterBarChip[] = datasetUploadFilterChips(filters).map((chip) => ({
    key: chip.axis,
    label: chip.label,
    removeLabel: "상태 필터 해제",
    onRemove: () => handleStatusGroupChange("all"),
  }));

  return (
    <FilterBar
      ariaLabel="업로드 현황 필터"
      chips={chips}
      onClearAll={() => handleStatusGroupChange("all")}
    >
      {showScope ? (
        <ContentSwitcher
          label="업로드 범위"
          options={uploadScopeOptions}
          value={filters.scope}
          onValueChange={(value) =>
            onChange({
              ...filters,
              scope: value === "all" ? "all" : ("me" as DatasetUploadListScope),
            })
          }
        />
      ) : null}
      <SingleSelectDropdown
        mode={hatcheryDropdownModes.filter}
        label="상태"
        neutralValue={STATUS_ALL_VALUE}
        value={filters.statusGroup === "all" ? STATUS_ALL_VALUE : filters.statusGroup}
        options={statusOptions}
        onValueChange={(next) => {
          const match = uploadStatusGroups.find((group) => group.value === next);
          handleStatusGroupChange(match ? match.value : "all");
        }}
      />
    </FilterBar>
  );
}
